import React, { FormEvent, Fragment, KeyboardEvent, MouseEvent, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { getContainer } from "../../ioc/IOCSetup";
import { t } from "../../i18n";
import { HttpError } from "../../http/Types";
import { ChatProgress, ChatReply, PageForms } from "../../services/ChatService";
import { FeatureFlags, useFeatureFlag } from "../../feature-flags/FeatureFlagContext";
import { renderMarkdown } from "./renderMarkdown";
import { ChatMessage, clearChatSession, loadChatSession, saveChatSession } from "./chatSession";
import { entityLabel, getPageContext } from "./pageContext";
import { isInternalLink } from "./entityRoutes";
import { AppliedChange, FormChangeCount, useFormBridge } from "./forms";
import { MessageChanges } from "./MessageChanges";
import { LiveTurn, LiveTurnView, withDelta, withStep } from "./LiveTurnView";
import { navigationOutcomeLine, turnOutcomeNote } from "./turnOutcome";
import "./AiChat.scss";

function touchedFormIds(applied: AppliedChange[]): string[] {
  return Array.from(new Set(applied.map((change) => change.formId)));
}

/**
 * Self-contained chat transcript + input. History lives server-side per conversationId,
 * so the component only keeps the rendered bubbles. Designed to be hosted by any
 * container (page today, drawer later) — takes no route props.
 */
export const ChatPanel = () => {
  const chatService = getContainer().getChatService();
  const history = useHistory();
  // The panel outlives navigation, so the context tracks whatever page is behind it right now.
  const pageContext = getPageContext(useLocation().pathname) ?? undefined;
  // The host only mounts the panel when the chat flag and the AI_CHAT permission hold, so
  // this second flag is the last of the three gates the form bridge sits behind.
  const formsEnabled = useFeatureFlag(FeatureFlags.AI_CHAT_FORMS);
  const bridge = useFormBridge(formsEnabled);
  const [initialSession] = useState(loadChatSession);
  const [conversationId, setConversationId] = useState(initialSession.conversationId);
  const [messages, setMessages] = useState<ChatMessage[]>(initialSession.messages);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [live, setLive] = useState<LiveTurn | null>(null);
  // Which forms each assistant message touched, keyed into this transcript — the bridge's own
  // records outlive "New chat" (markers and the audit header do), so the transcript keeps the
  // mapping. The live counts come from the bridge, so a line disappears on its own once the
  // admin edits, saves or reverts the form.
  const [formsByMessage, setFormsByMessage] = useState<Map<number, string[]>>(new Map());
  const messagesRef = useRef<HTMLDivElement>(null);
  // Aborted on unmount only: the drawer keeps the panel mounted while closed, so a turn streams on
  // behind it, and a turn the server already started finishes server-side either way.
  const unmountAbort = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    unmountAbort.current = controller;
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const el = messagesRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, pending, live]);

  useEffect(() => {
    saveChatSession({ conversationId, messages });
  }, [conversationId, messages]);

  const newChat = () => {
    const session = clearChatSession();
    setConversationId(session.conversationId);
    setMessages(session.messages);
    setFormsByMessage(new Map());
    setInput("");
  };

  const showReply = (response: ChatReply, replyIndex: number, pageForms: PageForms | undefined): void => {
    const reply: ChatMessage[] = [{ role: "assistant", text: response.reply }];
    const result = bridge?.applyReply(response.formUpdates, replyIndex);
    if (result && result.applied.length > 0) {
      setFormsByMessage((previous) => new Map(previous).set(replyIndex, touchedFormIds(result.applied)));
    }
    // The assistant can request navigation, but only to whitelisted internal pages.
    const followedPath = response.navigateTo && isInternalLink(response.navigateTo) ? response.navigateTo : undefined;
    const outcome = [turnOutcomeNote(pageForms, result, response.rejectedFormUpdates), navigationOutcomeLine(response.navigateTo, followedPath !== undefined)]
      .filter((line): line is string => line !== undefined)
      .join("\n");
    if (outcome) {
      reply.push({ role: "note", text: outcome });
    }
    setMessages((previous) => [...previous, ...reply]);
    if (followedPath) {
      history.push(followedPath);
    }
  };

  const sendMessage = (text: string) => {
    // The user bubble takes the next slot and the reply the one after. Sends are serialised by
    // `pending` and "New chat" is disabled meanwhile, so the slot is fixed before the reply lands.
    const replyIndex = messages.length + 1;
    const pageForms = bridge?.snapshot();
    const signal = unmountAbort.current?.signal;
    const progress: ChatProgress = {
      onDelta: (delta) => setLive((turn) => withDelta(turn, delta)),
      onStep: (step) => setLive((turn) => withStep(turn, step)),
    };
    const settle = (): void => {
      setLive(null);
      setPending(false);
    };
    setMessages((previous) => [...previous, { role: "user", text }]);
    setPending(true);
    chatService
      .sendMessage(conversationId, text, { pageContext, pageForms, progress, signal })
      .then((response) => {
        if (signal?.aborted) {
          return;
        }
        settle();
        showReply(response, replyIndex, pageForms);
      })
      .catch((error: HttpError) => {
        // The unmount abort rejects the turn too, once nothing is left to render it.
        if (signal?.aborted) {
          return;
        }
        settle();
        setMessages((previous) => [...previous, { role: "error", text: error.message }]);
      });
  };

  const trySend = () => {
    const text = input.trim();
    if (!text || pending) {
      return;
    }
    setInput("");
    sendMessage(text);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    trySend();
  };

  // Assistant links come from dangerouslySetInnerHTML, so react-router never sees them;
  // route internal ones through history to avoid a full page reload. Modifier- and
  // middle-clicks fall through so open-in-new-tab keeps working.
  const onMessagesClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }
    const anchor = (event.target as HTMLElement).closest("a[data-internal-link]");
    const href = anchor?.getAttribute("href");
    if (href) {
      event.preventDefault();
      history.push(href);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      trySend();
    }
  };

  const changesOfMessage = (index: number): FormChangeCount[] => {
    const formIds = formsByMessage.get(index);
    if (!bridge || !formIds) {
      return [];
    }
    return bridge.changeCounts.filter((count) => formIds.includes(count.formId));
  };

  const revertMessage = (index: number): void => {
    bridge?.revertMessage(index);
    setFormsByMessage((previous) => {
      const next = new Map(previous);
      next.delete(index);
      return next;
    });
  };

  return (
    <div
      className="ai-chat"
      data-testid="ai-chat"
    >
      <div className="ai-chat__toolbar">
        <button
          className="btn btn-sm btn-outline-secondary"
          type="button"
          onClick={newChat}
          disabled={pending}
          data-testid="ai-chat-new-chat"
        >
          {t("ai-chat.new-chat", undefined, "New chat")}
        </button>
      </div>
      <div
        className="ai-chat__messages"
        ref={messagesRef}
        data-testid="ai-chat-messages"
        onClick={onMessagesClick}
      >
        {messages.map((message, index) =>
          message.role === "assistant" ? (
            <Fragment key={index}>
              <div
                className="ai-chat__message ai-chat__message--assistant"
                data-testid="ai-chat-assistant-message"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(message.text) }}
              />
              <MessageChanges
                changes={changesOfMessage(index)}
                onRevertForm={(formId) => bridge?.revertForm(formId)}
                onRevertMessage={() => revertMessage(index)}
              />
            </Fragment>
          ) : (
            <div
              key={index}
              className={`ai-chat__message ai-chat__message--${message.role}`}
              data-testid={`ai-chat-${message.role}-message`}
            >
              {message.text}
            </div>
          ),
        )}
        <LiveTurnView
          pending={pending}
          turn={live}
        />
      </div>
      {pageContext && (
        <div
          className="ai-chat__context"
          data-testid="ai-chat-context-chip"
        >
          {t("ai-chat.viewing", undefined, "Viewing:")} {entityLabel(pageContext.entityType)} {pageContext.entityId}
        </div>
      )}
      {bridge?.activeFormTitle && (
        <div
          className="ai-chat__context"
          data-testid="ai-chat-forms-chip"
        >
          {/* React escapes the text itself; i18next escaping on top would show a title's "'" as "&#39;". */}
          {t("ai-chat.forms.chip", { title: bridge.activeFormTitle, interpolation: { escapeValue: false } }, "Forms: {{title}}")}
        </div>
      )}
      <form
        className="ai-chat__form"
        onSubmit={submit}
      >
        <textarea
          className="form-control"
          rows={2}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("ai-chat.placeholder", undefined, "Ask about players, payments, wallets… (Enter to send, Shift+Enter for newline)")}
          disabled={pending}
          data-testid="ai-chat-input"
        />
        <button
          className="btn btn-primary"
          type="submit"
          disabled={pending}
          data-testid="ai-chat-send"
        >
          {t("ai-chat.send", undefined, "Send")}
        </button>
      </form>
    </div>
  );
};
